import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8009/api';

// Create a configured axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Attach JWT token to every request automatically
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Auto-logout on 401/403
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // Don't logout on login attempts
            if (!error.config.url.includes('/auth/login')) {
                localStorage.removeItem('token');
                window.location.reload();
            }
        }
        return Promise.reject(error);
    }
);

// Define all API endpoints here
export const api = {
    // Auth Endpoints
    auth: {
        login: (username, password) => apiClient.post('/auth/login', { username, password }),
        listUsers: () => apiClient.get('/auth/users'),
        createUser: (data) => apiClient.post('/auth/users', data),
        deleteUser: (id) => apiClient.delete(`/auth/users/${id}`),
        resetPassword: (id, newPassword) => apiClient.post(`/auth/users/${id}/reset-password`, { password: newPassword }),
    },

    // Inventory Endpoints
    inventory: {
        getDashboardMetrics: () => apiClient.get('/inventory/dashboard-metrics'),
        getCurrent: () => apiClient.get('/inventory/current'),
        getKits: () => apiClient.get('/inventory/kits'),
        getSyncStatus: () => apiClient.get('/inventory/sync-status'),
        stopSync: () => apiClient.post('/inventory/stop-sync'),
        fetchManual: (type = 'all') => apiClient.post('/inventory/fetch-manual', { type }),
        getAlerts: (status, warehouse) => apiClient.get('/inventory/alerts', { params: { status, warehouse } }),
        getTrends: (days = 30) => apiClient.get('/inventory/trends', { params: { days } }),
        getClientOnHand: (warehouse = 'All') => apiClient.get('/inventory/client-onhand', { params: { warehouse } }),
        getClientMovement: (days = 30, warehouse = 'All') => apiClient.get('/inventory/client-movement', { params: { days, warehouse } }),
        
        // Settings Mapping
        getClientMap: () => apiClient.get('/inventory/settings/client-map'),
        updateClientMap: (data) => apiClient.post('/inventory/settings/client-map', data),
        getWarehouseMap: () => apiClient.get('/inventory/settings/warehouse-map'),
        updateWarehouseMap: (data) => apiClient.post('/inventory/settings/warehouse-map', data),
        
        getSnapshots: (page = 1, limit = 25, search = '', warehouse = 'All', date = '', client = 'All', days = 7, searchBySKU = true, searchByProduct = true) => apiClient.get('/inventory/snapshots', { params: { page, limit, search, warehouse, date, client, days, searchBySKU, searchByProduct } }),
        getForecast: (params = {}) => apiClient.get('/inventory/forecast', { params }),
        getPOReceipts: () => apiClient.get('/inventory/po-receipts'),
        getExportedFiles: () => apiClient.get('/inventory/exports'),
        downloadFileUrl: (filename) => {
            const token = localStorage.getItem('token');
            return `${API_BASE_URL}/inventory/exports/${filename}${token ? `?token=${token}` : ''}`;
        },
        exportLocationsUrl: (search = '', warehouse = 'All', location = '', client = 'All', status = 'All', type = 'All') => {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('export', 'csv');
            if (search) params.append('search', search);
            if (warehouse !== 'All') params.append('warehouse', warehouse);
            if (location) params.append('location', location);
            if (client !== 'All') params.append('client', client);
            if (status !== 'All') params.append('status', status);
            if (type !== 'All') params.append('type', type);
            if (token) params.append('token', token);
            return `${API_BASE_URL}/inventory/locations?${params.toString()}`;
        },
        getLocations: (page = 1, limit = 25, search = '', warehouse = 'All', location = '', client = 'All', status = 'All', type = 'All') => apiClient.get('/inventory/locations', { params: { page, limit, search, warehouse, location, client, status, type } }),
        getCycleCountOptions: () => apiClient.get('/inventory/cycle-count/options'),
        getSKUsByClient: (client) => apiClient.get('/inventory/cycle-count/skus-by-client', { params: { client } }),
        getCycleCountBins: (sku, warehouse) => apiClient.get('/inventory/cycle-count/bins', { params: { sku, warehouse } }),
        updateCycleCountBin: (data) => apiClient.post('/inventory/cycle-count/update', data),
        getAssignments: () => apiClient.get('/inventory/cycle-count/assignments'),
        createAssignment: (data) => apiClient.post('/inventory/cycle-count/assignments', data),
        deleteAssignment: (id) => apiClient.delete(`/inventory/cycle-count/assignments/${id}`),
        updateAssignmentStatus: (id, status) => apiClient.patch(`/inventory/cycle-count/assignments/${id}/status`, { status }),
        getClientMetrics: (month) => apiClient.get('/inventory/cycle-count/metrics', { params: { month } }),
        getPOMetrics: (month) => apiClient.get('/inventory/po-metrics', { params: { month } }),
    },

    // Webhook Endpoints
    webhooks: {
        // Usually webhooks are hit by external services, but defining here for completeness
        postShiphero: (payload) => apiClient.post('/webhooks/shiphero', payload),
    }
};

export default api;
